import { Assessment } from "@mui/icons-material";
import { ProgramRole, ReasonForDecline } from "../API";
import { SelectMenuOption } from "../common-ui/ReactHookFormSelectField";
import { AvailableCategory } from "../features/Activities/activitiesAPI";

// These choices will likely change to include more options
export const PGYSelectFields: SelectMenuOption[] = [
  { label: "(none)", value: "" },
  { label: "1", value: 1 },
  { label: "2", value: 2 },
  { label: "3", value: 3 },
  { label: "4", value: 4 },
  { label: "5", value: 5 },
  { label: "6", value: 6 },
  { label: "7", value: 7 },
  { label: "8", value: 8 },
  { label: "9", value: 9 },
  { label: "10", value: 10 },
  { label: "11", value: 11 },
  { label: "12", value: 12 }
];

export const ProgramRoleReportsFields = [
  { label: "Trainee", value: ProgramRole.Trainee },
  { label: "Attending", value: ProgramRole.Attending }
];

export const ProgramRoleSelectFields = [
  { label: "Clinician", value: "Clinician" },
  { label: "Program Administrator", value: ProgramRole.ProgramAdmin },
  {
    label: "Institutional Administrator",
    value: ProgramRole.Admin
  },
  // SE-1765: eligibility-only invitation roles. Offered on the New
  // User Invitation form when the target program's network has the
  // matching feature flag (see NewUserInvitation.tsx for the gating).
  { label: "Coach", value: "Coach" },
  { label: "Rotation Director", value: "RotationDirector" }
];

export const ProgramRoleSelectFieldsNew = [
  { label: "Trainee", value: ProgramRole.Trainee },
  { label: "Attending", value: ProgramRole.Attending },
  { label: "Program Administrator", value: ProgramRole.ProgramAdmin },
  {
    label: "Institutional Administrator",
    value: ProgramRole.Admin
  }
];

export const ReasonForDeclineSelectFields = [
  { label: "Incorrect Details", value: ReasonForDecline.IncorrectDetails },
  {
    label: "Insufficient Interaction",
    value: ReasonForDecline.InsufficientInteraction
  },
  { label: "Other", value: ReasonForDecline.Other }
];

export const TraineeTypeSelectFields: SelectMenuOption[] = [
  { label: "Categorical", value: "Categorical" },
  { label: "DesignatedPreliminary", value: "DesignatedPreliminary" },
  { label: "NonDesignatedPreliminary", value: "NonDesignatedPreliminary" },
  { label: "Fellow", value: "Fellow" },
  { label: "Medical Student", value: "MedicalStudent" }
];

export const UserFormErrors = {
  EmptyFirstName: "Please enter a value for the first name.",
  EmptyLastName: "Please enter a value for the last name.",
  EmptyEmail: "Please enter a value for the email address.",
  InvalidEmail: "Please enter a valid email address.",
  FirstNameTooLong: "The value for First Name must be 40 characters or less.",
  LastNameTooLong: "The value for Last Name must be 40 characters or less.",
  InvalidNpi:
    "NPIs must be a 10 digit number that begins with the number 1 or 2.",
  DuplicateProgramMembership: "Duplicated program and role.",
  TraineeInMoreThanOneProgram: "Trainees can only belong in one program.",
  RequiresUniqueEmail: "The new email address has not changed.",
  NPIRequired: "This user has a role that requires an NPI, please provide one.",
  JobTitleRequired:
    "This user has a role that requires a job title, please provide one.",
  TraineesOnlyOneMembership:
    "This trainee is already affiliated with another program. Trainees can not have multiple program memberships. If you have questions, please contact help@simpl.org",
  OneTraineeMembership: "A user can have only one Trainee membership.",
  TraineeCannotBeProgramAdmin:
    "A user cannot be both a Trainee and a Program Administrator in the same program.",
  CoachOrDirectorNotForTrainees:
    "A Trainee can't also be a Coach or Rotation Director in the same program.",
  AddExistingUserDuplicateMembership:
    "User is being added to the same program with the same role."
};

// Existing-user lookups (invite / add existing user) that failed, as opposed to
// finding no one.
export const ExistingUserLookupErrors = {
  EmailCheckFailed:
    "We couldn't check whether this email is already in SIMPL. Please try again or contact SIMPL Support at help@simpl.org.",
  NpiCheckFailed:
    "We couldn't check whether this NPI is already in SIMPL. Please try again or contact SIMPL Support at help@simpl.org.",
  UserDetailsNotLoaded:
    "We couldn't load this user's current programs, so they can't be added right now. Please try again or contact SIMPL Support at help@simpl.org."
};

export const UserNetworkErrors = {
  FetchAllUsersError:
    "Error fetching users. Please try again or contact your administrator.",
  FetchUserError:
    "Error fetching user. Please check the user ID and try again or contact your administrator.",
  UserNotFound: "User not found!",
  CreateUserError:
    "There was an error creating the user. Please try again or contact your administrator.",
  MultipleUsersWithSameEmail:
    "There are multiple users on the system with this email address. Contact your network administrator to consolidate the accounts.",
  UpdateEmailError: "There was an error updating this user's email address."
};

// Shared messages for ending/adding program memberships, including for
// unregistered users (SE-1722 / SE-1725). Kept here so EditUser and
// AddUserToProgram render identical copy from one source.
export const ProgramMembershipMessages = {
  // Shown inline for an unregistered user, and as the toast after a membership
  // is added to one — reminds the admin to set a usable email so they register.
  UnregisteredAddEmailReminder:
    "Update the user's email address as needed so they can register.",
  // 403 from update-program-memberships: caller doesn't administer the program.
  // Worded to not imply a visible program list — the 403 body carries none.
  Forbidden:
    "You don't administer one or more of these programs, so those changes weren't saved. Contact SIMPL Support at help@simpl.org if you need help.",
  // Any other failure (Cognito, server error, etc.).
  GenericError:
    "There was an error updating program memberships. Please try again or contact SIMPL Support at help@simpl.org.",
  // SE-1724: 400 from the Sys-Admin back-date floor — the end date can't be
  // before the user's last assessment activity in the program. `date` is the
  // earliest allowed end date (already formatted for display).
  EndDateBeforeFloor: (date: string) =>
    `This membership can't be ended before ${date} — the user has assessment activity in this program up to then. Pick ${date} or later.`
};

export const Regex = {
  Password:
    /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-])[A-Za-z\d^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-]{8,}$/,
  Email: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
  Numeric: /^\d+$/
};

export const pngs = {
  simplExample: "/simpl_app_example.png",
  appleStore: "/applestorelink.png",
  playStore: "/playstorelink.png",
  simplLogo: "/simpl_logo_wide.png",
  simplLogoFull: "/app-images/simpl_logo_full.png",
  simplLogoSquare: ""
};

export const s3Files = {
  BulkInviteProgramAdmin: `https://amplify-simpl2admin-prod-112713-deployment.s3.us-east-1.amazonaws.com/public/BulkInvite_ProgramAdmin.csv`,
  BulkInviteProgramAdminWithPrograms: `https://amplify-simpl2admin-prod-112713-deployment.s3.us-east-1.amazonaws.com/public/BulkInvite_ProgramAdmin_WithPrograms.csv`,
  BulkInviteSysAdmin: `https://amplify-simpl2admin-prod-112713-deployment.s3.us-east-1.amazonaws.com/public/BulkInvite_SysAdmin.csv`,
  // Stage-based bulk-invite templates. Upload the CSVs to the SAME bucket + `public/`
  // prefix, named exactly as below (matches the BulkInvite_* convention above).
  BulkInviteMedicalSchool: `https://amplify-simpl2admin-prod-112713-deployment.s3.us-east-1.amazonaws.com/public/BulkInvite_MedicalSchool.csv`,
  BulkInviteResidency: `https://amplify-simpl2admin-prod-112713-deployment.s3.us-east-1.amazonaws.com/public/BulkInvite_Residency.csv`,
  BulkInviteFellowship: `https://amplify-simpl2admin-prod-112713-deployment.s3.us-east-1.amazonaws.com/public/BulkInvite_Fellowship.csv`
};

export const createAssessmentSteps = {
  SelectParticipant: "Select Participant",
  SelectActivity: "Select Activity",
  SelectDateTime: "Select Date & Time",
  CreateAssessment: "Create Assessment",
  ConfirmProgram: "Confirm Program"
};

export const baseCreateAssessmentSteps = [
  createAssessmentSteps.SelectParticipant,
  createAssessmentSteps.SelectActivity,
  createAssessmentSteps.SelectDateTime,
  createAssessmentSteps.CreateAssessment
];

export const createAssessmentStepsWConfirmProgram = [
  createAssessmentSteps.SelectParticipant,
  createAssessmentSteps.SelectActivity,
  createAssessmentSteps.SelectDateTime,
  createAssessmentSteps.ConfirmProgram,
  createAssessmentSteps.CreateAssessment
];

export const createAssessmentButtonText = {
  Create: "CREATE ASSESSMENT",
  CreateAndStart: "CREATE & START ASSESSMENT"
};

export const createAssessmentErrors = {
  UserHasNoProgramMemberships:
    "You have no assigned Program Memberships. Please contact your Program Administrator.",
  ParticipantHasNoProgramMemberships:
    "The selected participant has no assigned Program Memberships. Please contact your Program Administrator.",
  SharedSponsorsHaveNoActivities:
    "The Sponsors that you share with the selected Participant have no available Activities. Please contact your Program Administrator.",
  NoSharedSponsors:
    "You have no Sponsors in common with the selected Participant so we cannot provide any Activities to choose from. Please contact your Program Administrator.",
  ParticipantHasNoNetworksOrSponsors:
    "The selected participant either has no associated Networks or no associated Sponsors. Please contact your Program Administrator.",
  UserHasNoNetworksOrSponsors:
    "You have either have no associated Networks or no associated Sponsors. Please contact your Program Administrator.",
  ParticipantsWithoutNecessaryRole:
    "One of the Activity participants does not have a required role to the selected program. Please contact your Program Administrator.",
  NoExpirationDurationHours:
    "The required ExpirationDurationHours is missing. Please contact your Program Administrator.",
  FailedToRetrieveAssessment: "Failed to retrieve the necessary Assessment.",
  AssessmentIdNotSet:
    "The selected activity has no assessmentId available. Please contact your Program Administrator.",
  FailedToCreateActivityParticipants:
    "There was an error creating the Activity Participants.",
  FailedToCreateActivityEvent:
    "There was an error creating the Activity Event.",
  MissingRequiredParticipantIds:
    "Missing one or more necessary IDs for the subject and/or rater.",
  SponsorHasNoActivities: "The specified sponsor has no activities. Skipping."
};

export const createAssessmentNotifications = {
  AssessmentCreatedSuccessfully: "Assessment created successfully!",
  NavTitle: "Are you sure you want to leave?",
  NavPrompt:
    "If you leave now, this assessment will not be created and your progress will be lost.",
  NavConfirm: "Yes, discard",
  NavCancel: "Cancel"
};

export const completeAssessmentNotifications = {
  NavTitle: "Are you sure you want to leave?",
  NavPrompt:
    "Your progress is auto-saved and you can resume later from In Progress Assessments",
  NavConfirm: "Leave",
  NavCancel: "Stay"
};

export const assessmentNotes = {
  AssessmentNotesTitle: "Send a Note",
  AssessmentNotesPlaceholder: "Optional",
  AssessmentNotesHelperText:
    "<li>Notes CANNOT include the patient's name or any other patient identifiers.</li>" +
    "<li>Notes CAN include details that will help your rater identify which patient encounter they are assessing or a request for specific feedback.</li>",
  AssessmentNotesHelperTextFromAttending:
    "<li>Do not include any patient identifiers (e.g., name or medical record number).</li>" +
    "<li>This note will be sent to {{user}} but will not be included as part of the completed assessment record.</li>" +
    '<li>You can include details to help the trainee remember the interaction (e.g., "Patient we saw together at 2 p.m.").</li>',
  AssessmentNotesHelperTextFromTrainee:
    "<li>Do not include any patient identifiers (e.g., name or medical record number).</li>" +
    "<li>This note will be sent to {{user}} but will not be included as part of the completed assessment record.</li>" +
    '<li>You can include requests for specific feedback (e.g., "Can you provide feedback on communication skills that I could focus on next?").</li>' +
    '<li>You can also include details to help the rater remember the interaction (e.g., "Patient we saw together at 2 p.m.").</li>'
};

export const MixpanelEvents = {
  AssessmentCreationStarted: "Assessment Creation Started",
  AssessmentCreatedAndStarted: "Assessment Created & Started",
  AssessmentRatingStarted: "Assessment Rating Started",
  AssessmentRatingCompleted: "Assessment Rating Completed",
  SignInSelected: "Sign In Selected",
  HomePageViewed: "Home Page Viewed"
};

export const AllCategory: AvailableCategory = {
  categoryId: "all",
  categoryName: "SHOW ALL"
};

export const DeepLinkValues = {
  ASSESSMENT_REQUESTED: "assessment-requested",
  ASSESSMENT_COMPLETED: "assessment-completed"
};
